import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
    CheckCircle, Calendar, MapPin, User, Ticket, QrCode, XCircle, 
    Clock, CreditCard, Smartphone, Mail, Phone, Share2, Copy, 
    Sparkles, Star, Shield, Award, Zap
} from 'lucide-react';
import { toast } from 'react-toastify';
import useApiRequest from './useApiRequest';
import Header from './Header';
import Footer from './Footer';
import { domain } from './Domain';

const GeneratedTickets = () => {
    const [searchParams] = useSearchParams();
    const ticketId = searchParams.get('id');
    const error = searchParams.get('error');
    const [ticketData, setTicketData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [eventInfo, setEventInfo] = useState(null);
    const { getRequest } = useApiRequest();

    useEffect(() => {
        if (error) {
            handleError(error);
        } else if (ticketId) {
            fetchTicketData();
        } else {
            toast.error('No ticket ID provided');
            setLoading(false);
        }
    }, [ticketId, error]);

    const handleError = (errorType) => {
        setLoading(false);
        switch (errorType) {
            case 'transaction_not_found':
                toast.error('Transaction not found. Please contact support.');
                break;
            case 'invalid_callback':
                toast.error('Invalid payment callback. Please contact support.');
                break;
            default:
                toast.error('An error occurred. Please contact support.');
        }
    };

    // Set event info from backend response
    useEffect(() => {
        if (ticketData?.event_details) {
            setEventInfo(ticketData.event_details);
        }
    }, [ticketData]);

    const fetchTicketData = async () => {
        try {
            setLoading(true);
            const response = await getRequest(`${domain}/generated-tickets?id=${ticketId}`);
            
            if (response.status === 200 && response.body) {
                setTicketData(response.body);
            } else {
                toast.error(response.body?.error || 'Failed to fetch ticket data');
            }
        } catch (error) {
            console.error('Error fetching ticket data:', error);
            toast.error('Failed to fetch ticket data');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const shareTicket = () => {
        if (navigator.share) {
            navigator.share({
                title: 'My Event Ticket',
                text: `Check out my ticket for ${eventInfo?.event_plot_name || 'the event'}!`,
                url: window.location.href
            });
        } else {
            copyToClipboard(window.location.href);
        }
    };


    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="relative">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600 mx-auto"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-purple-600 animate-pulse" />
                        </div>
                    </div>
                    <p className="mt-6 text-gray-700 text-lg font-medium">Loading your tickets...</p>
                    <p className="mt-2 text-gray-500">Preparing your digital tickets</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-rose-100 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <XCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-3">Payment Error</h2>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                        {error === 'transaction_not_found' 
                            ? 'We couldn\'t find your transaction. Please contact our support team for assistance.'
                            : error === 'invalid_callback'
                            ? 'There was an issue processing your payment callback. Please contact support.'
                            : 'An unexpected error occurred. Please contact our support team.'
                        }
                    </p>
                    <div className="space-y-3">
                        <button 
                            onClick={() => window.location.href = '/events'}
                            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg"
                        >
                            Browse Events
                        </button>
                        <button 
                            onClick={() => window.location.href = '/contact'}
                            className="w-full bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-all duration-200"
                        >
                            Contact Support
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!ticketData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-rose-100 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto px-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                        <XCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-3">Ticket Not Found</h2>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                        The requested ticket could not be found. Please check your ticket ID and try again.
                    </p>
                    <button 
                        onClick={() => window.location.href = '/'}
                        className="bg-gradient-to-r from-orange-500 to-pink-500 !text-white px-8 py-3 rounded-xl hover:from-orange-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-lg font-medium"
                    >
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    const { ticket_purchase, ticket_info } = ticketData;
    const customerData = ticket_purchase.data;
    const paymentData = ticket_purchase.paymentData;

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
            <Header />
            
            <div className="container mx-auto px-4 py-8 max-w-6xl">
                {/* Success Header */}
                <div className="text-center mb-12">
                    <div className="relative inline-block">
                        <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-12 h-12 text-teal-500" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
                            <Sparkles className="w-4 h-4 !text-white" />
                        </div>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 bg-clip-text text-transparent mb-4">
                        Tickets Generated Successfully!
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                        🎉 Your tickets have been sent to your email and are ready for download. 
                        Get ready for an amazing experience!
                    </p>
                </div>

                {/* Event Information Card */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 mb-8 border border-white/20">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                            <Ticket className="w-6 h-6 !text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800">Event Information</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Event Details */}
                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                    <Calendar className="w-5 h-5 !text-white" />
                                </div>
                        <div>
                                    <h3 className="font-bold text-gray-800 text-lg mb-2">
                                        {eventInfo ? eventInfo.event_plot_name : customerData.description}
                                    </h3>
                                    {eventInfo && (
                                        <div className="space-y-2">
                                            <p className="flex items-center gap-2 text-gray-600">
                                            <MapPin className="w-4 h-4 text-orange-500" />
                                                {eventInfo.venue_name}
                                        </p>
                                            <p className="flex items-center gap-2 text-gray-600">
                                                <Clock className="w-4 h-4 text-orange-500" />
                                            {formatDate(eventInfo.start_date_event)}
                                        </p>
                                        {eventInfo.end_date_event && (
                                                <p className="text-sm text-gray-500">
                                                Ends: {formatDate(eventInfo.end_date_event)}
                                            </p>
                                        )}
                                        {eventInfo.age_restriction && (
                                                <p className="text-sm text-gray-500">
                                                Age Restriction: {eventInfo.age_restriction}
                                            </p>
                                        )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Purchase Details */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                                    <CreditCard className="w-5 h-5 !text-white" />
                                </div>
                                <h3 className="font-bold text-gray-800 text-lg">Purchase Details</h3>
                            </div>
                            
                            <div className="space-y-3">
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Order ID:</span>
                                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                                        {ticket_purchase.order_id}
                                    </span>
                                </div>
                                
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Status:</span>
                                    <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-semibold flex items-center gap-1">
                                        <Shield className="w-3 h-3" />
                                        {ticket_purchase.status}
                                    </span>
                                </div>
                                
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Amount:</span>
                                    <span className="font-bold text-lg text-green-600">
                                        UGX {parseFloat(ticket_purchase.amount).toLocaleString()}
                                    </span>
                                </div>
                                
                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                    <span className="text-gray-600 font-medium">Payment:</span>
                                    <span className="text-gray-800 font-medium">{customerData.paymentMode}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer Information */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 mb-8 border border-white/20">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                            <User className="w-6 h-6 !text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800">Customer Information</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                                <User className="w-5 h-5 !text-white" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Name</p>
                                <p className="font-semibold text-gray-800">
                                    {customerData.firstName} {customerData.lastName}
                                </p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                                <Mail className="w-5 h-5 !text-white" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Email</p>
                                <p className="font-semibold text-gray-800">{customerData.email}</p>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                                <Phone className="w-5 h-5 !text-white" />
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Phone</p>
                                <p className="font-semibold text-gray-800">{customerData.phoneNumber}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Digital Ticket Card */}
                <div className="bg-gradient-to-br from-white via-purple-50 to-pink-50 rounded-3xl shadow-2xl p-8 mb-8 border border-white/20 relative overflow-hidden">
                    {/* Decorative Elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-200/30 to-pink-200/30 rounded-full -translate-y-16 translate-x-16"></div>
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-200/30 to-cyan-200/30 rounded-full translate-y-12 -translate-x-12"></div>
                    
                    <div className="relative z-10">
                        <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                                    <Award className="w-8 h-8 !text-white" />
                                </div>
                                <div>
                                    <h2 className="text-3xl font-bold text-gray-800">Your Digital Ticket</h2>
                                    <p className="text-gray-600">Quantity: {customerData.qty} ticket(s)</p>
                                </div>
                            </div>
                            
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Ticket Details */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                    <Star className="w-5 h-5 text-yellow-500" />
                                        Ticket Details
                                    </h3>
                                
                                <div className="space-y-4">
                                    <div className="p-4 bg-white/60 rounded-xl border border-white/40">
                                        <p className="text-sm text-gray-600 mb-1">Ticket Holder</p>
                                        <p className="font-bold text-gray-800 text-lg">
                                            {customerData.firstName} {customerData.lastName}
                                        </p>
                                    </div>
                                    
                                    {paymentData.seatNumbers && paymentData.seatNumbers.length > 0 && (
                                        <div className="p-4 bg-white/60 rounded-xl border border-white/40">
                                            <p className="text-sm text-gray-600 mb-1">Seat Number(s)</p>
                                            <p className="font-bold text-gray-800">
                                                {paymentData.seatNumbers.join(', ')}
                                            </p>
                                        </div>
                                    )}
                                    
                                     <div className="p-4 bg-white/60 rounded-xl border border-white/40">
                                         <p className="text-sm text-gray-600 mb-1">Ticket Type</p>
                                         <p className="font-bold text-gray-800">
                                             {ticket_info ? `${ticket_info.ticket_type?.name || 'General'} - ${ticket_info.ticket_category?.name || 'Admission'}` : 'General Admission'}
                                         </p>
                                     </div>
                                </div>
                            </div>
                            
                             {/* QR Code Section */}
                             <div className="flex items-center justify-center gap-6">
                                 {/* QR Code */}
                                 <div className="relative">
                                     <div className="w-48 h-48 bg-white rounded-2xl shadow-2xl flex items-center justify-center border-4 border-gradient-to-br from-purple-200 to-pink-200">
                                         {customerData.img ? (
                                             <img 
                                                 src={customerData.img} 
                                                 alt="QR Code" 
                                                 className="w-full h-full object-contain p-4"
                                             />
                                         ) : (
                                             <QrCode className="w-24 h-24 text-gray-400" />
                                         )}
                                     </div>
                                     
                                 </div>
                                 
                                 {/* Action Buttons */}
                                 <div className="flex flex-col gap-3">
                                     <button
                                         onClick={() => copyToClipboard(paymentData.reference_id)}
                                         className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 px-4 py-2.5 rounded-lg hover:from-blue-100 hover:to-cyan-100 transition-all duration-200 font-medium text-sm border border-blue-200"
                                     >
                                         <Copy className="w-4 h-4" />
                                         Copy Ref ID
                                     </button>
                                     
                                     <button
                                         onClick={() => copyToClipboard(paymentData.financialTransactionId)}
                                         className="flex items-center gap-2 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 px-4 py-2.5 rounded-lg hover:from-purple-100 hover:to-pink-100 transition-all duration-200 font-medium text-sm border border-purple-200"
                                     >
                                         <Copy className="w-4 h-4" />
                                         Copy TXN ID
                                     </button>
                                     
                                     <button
                                         onClick={shareTicket}
                                         className="flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-4 py-2.5 rounded-lg hover:from-green-100 hover:to-emerald-100 transition-all duration-200 font-medium text-sm border border-green-200"
                                     >
                                         <Share2 className="w-4 h-4" />
                                         Share Ticket
                                     </button>
                                 </div>
                             </div>
                        </div>
                        
                        {/* Ticket Holder Names Section - Below QR Code */}
                        {paymentData.names && paymentData.names.length > 0 && (
                            <div className="mt-8">
                                <div className="p-6 bg-white/60 rounded-xl border border-white/40">
                                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <User className="w-5 h-5 text-blue-500" />
                                        Ticket Holders
                                    </h4>
                                    <div className="space-y-3">
                                        {paymentData.names.map((name, index) => (
                                            <div key={index} className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
                                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                                                    <span className="text-white text-sm font-bold">{index + 1}</span>
                                                </div>
                                                <p className="font-semibold text-gray-800 text-lg">{name}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

            </div>
            
            <Footer />
        </div>
    );
};

export default GeneratedTickets;
